import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash, Barbell, ForkKnife, CircleNotch } from "@phosphor-icons/react";

const pad = (n) => String(n).padStart(2, "0");
const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const nowHM = () => { const d = new Date(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };

export default function SavePlanDialog({ open, onOpenChange, plan, editId, onSaved }) {
  const [kind, setKind] = useState("workout");
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([]);
  const [dt, setDt] = useState(todayISO());
  const [tm, setTm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !plan) return;
    setKind(plan.kind === "meal" ? "meal" : "workout");
    setTitle(plan.title || "");
    setItems((plan.items || []).map((i) => ({ name: i.name, detail: i.detail || "", done: i.done || false })));
    setDt(plan.date || todayISO());
    setTm(plan.time || (plan.kind === "meal" ? nowHM() : ""));
  }, [open, plan]);

  const setItem = (i, field, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [field]: v } : it)));

  const save = async () => {
    const clean = items.filter((i) => i.name.trim());
    if (!title.trim() || !clean.length) { toast.error("Заполните название и хотя бы один пункт"); return; }
    setSaving(true);
    try {
      if (kind === "meal") {
        const text = `${title}: ` + clean.map((i) => (i.detail ? `${i.name} (${i.detail})` : i.name)).join(", ");
        const { data } = await api.post("/nutrition/manual", { text, date: dt, time: tm || "" });
        toast.success(data.meal ? "Блюдо записано в «Питание» с расчётом КБЖУ" : (data.comment || "Не удалось распознать еду"));
      } else if (editId) {
        await api.put(`/workouts/${editId}`, { title, items: clean, date: dt, time: tm || null });
        toast.success("Тренировка обновлена");
      } else {
        await api.post("/workouts", { title, items: clean, date: dt, time: tm || null });
        toast.success("Тренировка сохранена в «Здоровье» — отмечайте упражнения по мере выполнения");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal max-h-[85vh] overflow-y-auto" data-testid="save-plan-dialog">
        <DialogHeader><DialogTitle>{editId ? "Редактировать тренировку" : "Сохранить план"}</DialogTitle></DialogHeader>
        {!editId && (
          <div className="flex gap-2">
            {[["workout", "Тренировка", Barbell], ["meal", "Блюдо", ForkKnife]].map(([id, label, Icon]) => (
              <button key={id} onClick={() => setKind(id)} data-testid={`plan-kind-${id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${kind === id ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                <Icon size={13} weight="fill" /> {label}
              </button>
            ))}
          </div>
        )}
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Название" data-testid="plan-title-input" />
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`plan-item-${i}`}>
              <Input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)}
                placeholder={kind === "meal" ? "Продукт / блюдо" : "Упражнение"} className="flex-1" data-testid={`plan-item-name-${i}`} />
              <Input value={it.detail} onChange={(e) => setItem(i, "detail", e.target.value)}
                placeholder={kind === "meal" ? "порция" : "3×15"} className="w-28" data-testid={`plan-item-detail-${i}`} />
              <button onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))} data-testid={`plan-item-remove-${i}`}
                className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash size={15} /></button>
            </div>
          ))}
          <button onClick={() => setItems((arr) => [...arr, { name: "", detail: "", done: false }])} data-testid="plan-item-add"
            className="flex items-center gap-1 text-xs text-primary hover:underline"><Plus size={13} /> Добавить пункт</button>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1">Дата</label>
            <Input type="date" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="plan-date-input" />
          </div>
          <div className="w-32">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1">Время</label>
            <Input type="time" value={tm} onChange={(e) => setTm(e.target.value)} data-testid="plan-time-input" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="w-full" data-testid="plan-save-button">
          {saving ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : null}
          {kind === "meal" ? "Записать в Питание" : editId ? "Сохранить изменения" : "Сохранить в Здоровье"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
